import type {
    FilteredValue,
    Form,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormPayloadSerializer } from '@fitogest/reactive-form-builder/utils';

import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { StockageModel } from 'src/app/models/stock/stokage.model';

export interface StockEntradaMirrorPayloadContext {
    readonly formFields: Form<StockageModel>;
    readonly model: ReactiveFormValue<StockageModel>;
    readonly parcelas: ReadonlyArray<FilteredValue<ParcelasModel>>;
    readonly rawValues?: Record<string, unknown>;
    readonly fallbackClienteId?: unknown;
    readonly fallbackUsuarioId?: unknown;
}

export interface StockEntradaMirrorPayloadResult {
    readonly payload: Record<string, unknown>;
    readonly ubicacionOrigen: string;
    readonly ubicacionDestino: string;
}

/**
 * Builds the synthetic stock exit payload created after a MOV entry is saved.
 */
export function buildStockEntradaMirrorPayload(context: StockEntradaMirrorPayloadContext): StockEntradaMirrorPayloadResult | null {
    if (String(context.model.tipo ?? '') !== 'MOV') {
        return null;
    }

    const ubicacionOrigen = extractStockEntradaMirrorId(context.model.ubicacion);
    const ubicacionDestino = extractStockEntradaMirrorId(context.model.ubicacion_destino);

    if (ubicacionOrigen === '' || ubicacionDestino === '') {
        return null;
    }

    const idFincaOrigen = extractStockEntradaFincaFromParcela(context.parcelas, ubicacionOrigen);
    const idCliente = extractStockEntradaMirrorId(context.rawValues?.['id_cliente'])
        || extractStockEntradaMirrorId(context.fallbackClienteId);
    const idUsuario = extractStockEntradaMirrorId(context.rawValues?.['id_usuario'])
        || extractStockEntradaMirrorId(context.fallbackUsuarioId);

    const { id: _omittedId, ...modelWithoutId } = context.model;

    const mirrorBase: Record<string, unknown> = {
        ...modelWithoutId,
        tipo: 'S',
        ubicacion: ubicacionDestino,
        ubicacion_destino: ubicacionOrigen,
        id_finca: idFincaOrigen,
        id_cliente: idCliente,
        id_usuario: idUsuario,
        id_comercio: extractStockEntradaMirrorId(context.model['id_comercio']),
    };

    return {
        payload: ReactiveFormPayloadSerializer.serialize(context.formFields, mirrorBase),
        ubicacionOrigen,
        ubicacionDestino,
    };
}

/**
 * Normalizes plain and dropdown object values to the backend id used by the stock endpoint.
 */
export function extractStockEntradaMirrorId(value: unknown): string {
    if (value == null) {
        return '';
    }

    if (typeof value === 'object') {
        return String((value as Record<string, unknown>)['id'] ?? '');
    }

    return String(value);
}

function extractStockEntradaFincaFromParcela(
    parcelas: ReadonlyArray<FilteredValue<ParcelasModel>>,
    parcelaId: string,
): string {
    const found = parcelas.find(
        (item) => String(item.value?.id ?? '') === parcelaId
    );

    return String(found?.value?.id_finca ?? '');
}
